import { Component, signal, OnInit, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

const DISMISS_KEY = 'pwa-install-prompt-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * InstallPromptComponent listens for the `beforeinstallprompt` event and shows
 * a banner encouraging the user to install the PWA.
 *
 * Features:
 * - Shows install banner when browser fires beforeinstallprompt
 * - Install button triggers the native install prompt
 * - Dismiss button hides banner for 7 days (stored in localStorage)
 * - On iOS (where beforeinstallprompt is not supported), shows manual instructions
 */
@Component({
    selector: 'app-install-prompt',
    imports: [TranslateModule],
    template: `
    @if (showBanner()) {
      <div class="install-banner" role="banner" aria-live="polite">
        <div class="install-content">
          @if (isIos()) {
            <span class="install-text">{{ 'app.install_ios' | translate }}</span>
          } @else if (isManualInstall()) {
            <span class="install-text">{{ 'app.install_manual' | translate }}</span>
          } @else {
            <span class="install-text">{{ 'app.install_prompt' | translate }}</span>
            <button class="install-btn" (click)="installApp()">
              {{ 'app.install_button' | translate }}
            </button>
          }
        </div>
        <button class="dismiss-btn" (click)="dismiss()" aria-label="Dismiss">×</button>
      </div>
    }
  `,
    styles: [`
    .install-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 14px;
      background: #e3f2fd;
      border-bottom: 1px solid #bbdefb;
      font-size: 0.85rem;
      color: #1565c0;
      gap: 8px;
    }

    .install-content {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      flex-wrap: wrap;
    }

    .install-text {
      flex: 1;
      min-width: 150px;
    }

    .install-btn {
      background: #1565c0;
      color: #fff;
      border: none;
      padding: 6px 14px;
      border-radius: 6px;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.2s;
    }

    .install-btn:hover {
      background: #0d47a1;
    }

    .dismiss-btn {
      background: none;
      border: none;
      font-size: 1.2rem;
      cursor: pointer;
      color: #1565c0;
      padding: 4px 8px;
      border-radius: 4px;
      line-height: 1;
      flex-shrink: 0;
    }

    .dismiss-btn:hover {
      background: rgba(21, 101, 192, 0.1);
    }
  `]
})
export class InstallPromptComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);

  readonly showBanner = signal(false);
  private deferredPrompt: any = null;
  private boundHandler: ((e: Event) => void) | null = null;
  private isIosDevice = false;
  private showManualInstructions = false;

  isIos(): boolean {
    return this.isIosDevice;
  }

  isManualInstall(): boolean {
    return this.showManualInstructions;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Check if dismissed recently
    if (this.isDismissed()) return;

    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    // Also check iOS standalone
    if ((navigator as any).standalone === true) return;

    // Detect iOS
    const ua = navigator.userAgent;
    this.isIosDevice = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;

    if (this.isIosDevice) {
      // iOS doesn't fire beforeinstallprompt, show instructions directly
      this.showBanner.set(true);
      return;
    }

    // Check if beforeinstallprompt was already captured in main.ts (before Angular bootstrapped)
    const earlyEvent = (window as any).__pwaInstallPromptEvent;
    if (earlyEvent) {
      this.deferredPrompt = earlyEvent;
      this.showBanner.set(true);
      delete (window as any).__pwaInstallPromptEvent;
    }

    // Listen for the event (may fire after component init)
    this.boundHandler = (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showManualInstructions = false;
      this.showBanner.set(true);
    };
    window.addEventListener('beforeinstallprompt', this.boundHandler);

    // Fallback: if beforeinstallprompt hasn't fired within 3 seconds,
    // show manual install instructions (covers Firefox, Samsung Internet, etc.)
    if (!this.deferredPrompt) {
      setTimeout(() => {
        if (!this.deferredPrompt && !this.showBanner() && !this.isDismissed()) {
          this.showManualInstructions = true;
          this.showBanner.set(true);
        }
      }, 3000);
    }
  }

  ngOnDestroy(): void {
    if (this.boundHandler && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('beforeinstallprompt', this.boundHandler);
    }
  }

  async installApp(): Promise<void> {
    if (!this.deferredPrompt) return;

    this.deferredPrompt.prompt();
    const result = await this.deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      this.showBanner.set(false);
    }
    this.deferredPrompt = null;
  }

  dismiss(): void {
    this.showBanner.set(false);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    }
  }

  private isDismissed(): boolean {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed) return false;
    const dismissedAt = parseInt(dismissed, 10);
    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  }
}
